import React, { useEffect, useState } from 'react';

import { Container, Paper } from '@mui/material';
import { observer } from 'mobx-react';
import { useToggle } from 'react-use';
import { useErrorBoundary } from 'react-error-boundary';

import { YesNoBtnRow } from '@infoods/lifebase-ui';
import classes from './NgzSelfBillingFailedPayment.module.scss';
import { angularizeWithProviders } from '../../../../shared/providers';
import { logout } from '../../../../shared/utils/userInfoUtils/logout/logout';
import {
  PaymentMethodType,
  PAYMENT_METHODS,
} from './NgzSelfBillingFailedPayment.settings';
import { TitleBlock } from './TitleBlock/TitleBlock';
import { SelectPaymentMethod } from './SelectPaymentMethod/SelectPaymentMethod';
import { StripeElementsProvider } from '../../../../shared/components/stripe/StripeElementsProvider/StripeElementsProvider';
import { stripeService } from '../../../services/stripeService';
import { loggedInUserDataStore } from '../../../../shared/stores/loggedInUserDataStore';
import { CurrentCardInfo } from './CurrentCardInfo/CurrentCardInfo';
import { SelfBillingPaymentIntent } from '../../../interfaces/selfBillingStripe';
import { CheckoutForm } from './CheckoutForm/CheckoutForm';
import { entityPaymentService } from '../../../../shared/services/entity/entityPaymentService';
import { EntityPaymentMethodDetails } from '../../../../shared/interfaces/entityPaymentMethodDetails';
import { RESPONSE_STATUSES } from '../../../../shared/constants/RESPONSE_STATUSES';
import { updateUserInfo } from '../../../../shared/utils/userInfoUtils/updateUserInfo/updateUserInfo';
import { PaymentResult } from './PaymentResult/PaymentResult';
import { SELF_BILLING_PAYMENT_STATUS } from '../../../../shared/constants/entity/selfBillingPaymentStatus';
import { goToHomePage } from '../../../../shared/utils/angularUtils';

const formId = 'self-billing-failed-payment-form-id';

const NgzSelfBillingFailedPayment = observer(() => {
  const { showBoundary } = useErrorBoundary();
  const [showPaymentResult, toggleShowPaymentResult] = useToggle(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>(
    PAYMENT_METHODS.NEW_CARD,
  );
  const [paymentIntent, setPaymentIntent] = useState<SelfBillingPaymentIntent | null>(
    null,
  );
  const [paymentMethodDetails, setPaymentMethodDetails] =
    useState<EntityPaymentMethodDetails | null>(null);

  const entityId = loggedInUserDataStore.getEntityId();

  const getPaymentMethodDetails = async () => {
    const response = await entityPaymentService.getEntityPaymentMethodDetails(entityId, {
      ignoreErrorCodes: [404, RESPONSE_STATUSES.PAYMENT_REQUIRED], // PAYMENT_REQUIRED needs to fix potential loop
    });

    setPaymentMethodDetails(response);
  };

  const getPaymentIntent = async () => {
    if (!entityId) {
      throw new Error('Entity ID is not found');
    }

    try {
      const intent = await stripeService.getSelfBillingPaymentIntent(entityId, {
        ignoreErrorCodes: [RESPONSE_STATUSES.PAYMENT_REQUIRED],
      });

      setPaymentIntent(intent);
    } catch (error: any) {
      showBoundary(new Error(error.response?.data?.message || 'Unknown error'));
    }
  };

  const checkPaymentStatus = async () => {
    const { selfBillingEntityPaymentStatus } =
      await entityPaymentService.getSelfBillingPaymentStatus(entityId);

    if (selfBillingEntityPaymentStatus === SELF_BILLING_PAYMENT_STATUS.PAYMENT_REQUIRED) {
      return true;
    } else {
      updateUserInfo({
        entity: { selfBillingEntityPaymentStatus },
      });
      goToHomePage();
      return false;
    }
  };

  const getPaymentData = async () => {
    const isPaymentRequired = await checkPaymentStatus();
    if (isPaymentRequired) {
      getPaymentIntent();
      getPaymentMethodDetails();
    }
  };

  useEffect(() => {
    getPaymentData();
  }, []);

  const handlePaymentSuccess = async () => {
    const { selfBillingEntityPaymentStatus } =
      await entityPaymentService.getSelfBillingPaymentStatus(entityId);

    updateUserInfo({
      entity: { selfBillingEntityPaymentStatus },
    });
    toggleShowPaymentResult(true);
  };

  const changePaymentMethod = async (method: PaymentMethodType) => {
    if (method === PAYMENT_METHODS.NEW_CARD) {
      // need re-get payment intent for new card (save new card for future problem)
      // because when use retry api it change payment intent config in stripe and need set it back to save new card for future use
      await getPaymentIntent();
    }

    setPaymentMethod(method);
  };

  if (!paymentIntent) {
    return null;
  }

  return (
    <Container className={classes.container}>
      {showPaymentResult ? (
        <PaymentResult
          entityId={entityId}
          paymentIntentId={paymentIntent.paymentIntentId}
        />
      ) : (
        <Paper variant="roundedBox" className={classes.box}>
          <TitleBlock orderAmount={+paymentIntent.orderAmount} />
          {paymentMethodDetails && (
            <SelectPaymentMethod
              paymentMethod={paymentMethod}
              setPaymentMethod={changePaymentMethod}
            />
          )}
          {paymentMethod === PAYMENT_METHODS.NEW_CARD && (
            <StripeElementsProvider
              publishableKey={paymentIntent.publishableKey}
              connectedAccountId={paymentIntent.connectedAccountId}
              elementsOptions={{ clientSecret: paymentIntent.clientSecret }}
            >
              <CheckoutForm
                formId={formId}
                entityId={entityId}
                onPaymentSuccess={handlePaymentSuccess}
              />
            </StripeElementsProvider>
          )}
          {paymentMethod === PAYMENT_METHODS.RETRY_WITH_CURRENT_CARD && (
            <CurrentCardInfo
              formId={formId}
              entityId={entityId}
              paymentMethodDetails={paymentMethodDetails}
              onPaymentSuccess={handlePaymentSuccess}
            />
          )}
          <YesNoBtnRow
            yesText="Confirm"
            formId={formId}
            noText="Log out"
            noHandler={() => logout()}
            size="medium"
          />
        </Paper>
      )}
    </Container>
  );
});

angularizeWithProviders(NgzSelfBillingFailedPayment, 'ngzSelfBillingFailedPayment', {});

export { NgzSelfBillingFailedPayment };
